import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Nav, Row } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";
import "containers/slm/shared/SLMSearchListCheckbox.css";

const integerFormatter = new Intl.NumberFormat("en-AU", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const currencyFormatter = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function parsePrice(value) {
  if (value === null || value === undefined) {
    return 0;
  }

  const parsed = Number(String(value).replaceAll(",", "").replace("$", ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function SLMSaleList({ isAuthenticated, data, search_count }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [sales, setSales] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    setSales(Array.isArray(data) ? data : []);
  }, [data]);

  const columnHelper = useMemo(() => createColumnHelper(), []);

  const createId = useMemo(() => createElementIdFactory("slm-sale-search"), []);
  const tableElementIds = useMemo(() => createReusableTableElementIds(createId), [createId]);

  const handleCheckboxChange = (rowIndex, checked) => {
    setSales((previous) =>
      previous.map((record, index) => {
        if (index !== rowIndex) {
          return record;
        }

        return { ...record, selected: checked, modified: true };
      })
    );
  };

  const selectAll = () => {
    setSales((previous) => previous.map((record) => ({ ...record, selected: true, modified: true })));
  };

  const deselectAll = () => {
    setSales((previous) => previous.map((record) => ({ ...record, selected: false, modified: true })));
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("selected", {
        id: "selected",
        header: "",
        enableSorting: false,
        size: 60,
        meta: { align: "center" },
        cell: (info) => (
          <div className="slm-search-list-checkbox-cell">
            <input
              checked={Boolean(info.getValue())}
              type="checkbox"
              className="slm-search-list-checkbox-input"
              onChange={(event) => handleCheckboxChange(info.row.index, event.target.checked)}
            />
          </div>
        ),
      }),
      columnHelper.accessor("sale_id", {
        id: "sale_id",
        header: "Sale ID",
        size: 80,
        cell: (info) => {
          const clickedId = info.getValue();
          const selectedIds = (sales || []).filter((sale) => sale.selected).map((sale) => sale.sale_id);
          const browseIds = selectedIds.length > 0 ? selectedIds : [clickedId];
          const targetPath = `/slmsales/${clickedId}`;

          return (
            <a
              href={targetPath}
              onClick={(event) => {
                event.preventDefault();
                try {
                  sessionStorage.setItem("slm-sale-browse-ids", JSON.stringify(browseIds));
                } catch (error) {
                  // Ignore storage failures (e.g. private mode restrictions) and continue with route state.
                }
                navigate(targetPath, { state: { data: browseIds } });
              }}
            >
              {clickedId}
            </a>
          );
        },
      }),
      columnHelper.accessor("sale_type", {
        id: "sale_type",
        header: "SOLD/For Sale",
        size: 120,
      }),
      columnHelper.accessor("building_name", {
        id: "building_name",
        header: "Building Name",
        size: 180,
      }),
      columnHelper.accessor("street_address_full", {
        id: "street_address_full",
        header: "Address",
        size: 220,
      }),
      columnHelper.accessor("suburb", {
        id: "suburb",
        header: "Suburb",
        size: 120,
      }),
      columnHelper.accessor("state_code", {
        id: "state_code",
        header: "State",
        size: 80,
      }),
      columnHelper.accessor("postcode", {
        id: "postcode",
        header: "Postcode",
        size: 90,
      }),
      columnHelper.accessor("stat_date", {
        id: "stat_date",
        header: "Stat Date",
        cell: (info) => formatDate(info.getValue()),
        sortingFn: (rowA, rowB, columnId) => {
          const aValue = rowA.getValue(columnId);
          const bValue = rowB.getValue(columnId);
          const aDate = new Date(aValue || 0).getTime();
          const bDate = new Date(bValue || 0).getTime();
          return aDate === bDate ? 0 : aDate > bDate ? 1 : -1;
        },
        size: 100,
      }),
      columnHelper.accessor("sale_price", {
        id: "sale_price",
        header: "Price",
        size: 120,
      }),
      columnHelper.accessor("floor_area", {
        id: "floor_area",
        header: "Bldg Area",
        cell: (info) => integerFormatter.format(Number(info.getValue()) || 0),
        size: 110,
      }),
      columnHelper.accessor((row) => {
        const price = parsePrice(row.sale_price);
        const area = Number(row.floor_area || 0);
        if (area <= 0) {
          return 0;
        }
        return price / area;
      }, {
        id: "price_per_sqm",
        header: "Price Per SQM Bldg",
        cell: (info) => currencyFormatter.format(Number(info.getValue()) || 0),
        size: 140,
      }),
      columnHelper.accessor("yield", {
        id: "yield",
        header: "Yield",
        size: 90,
      }),
      columnHelper.accessor("broad_space_use_code", {
        id: "broad_space_use_code",
        header: "Broad Space",
        size: 130,
      }),
      columnHelper.accessor("space_use_name", {
        id: "space_use_name",
        header: "Space Use",
        size: 130,
      }),
      columnHelper.accessor("agent", {
        id: "agent",
        header: "Agent",
        size: 140,
      }),
      columnHelper.accessor("agency", {
        id: "agency",
        header: "Agency",
        size: 140,
      }),
      columnHelper.accessor("purchaser", {
        id: "purchaser",
        header: "Buyer",
        size: 160,
      }),
    ],
    [columnHelper, navigate, sales]
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="SLMSaleList slm-search-list">
      <Row>
        <Col className="text-left">
          <Nav className="justify-content-start">
            <Nav.Item>
              <Button variant="primary" type="button" size="sm" onClick={selectAll}>
                Select All
              </Button>{" "}
            </Nav.Item>
            <Nav.Item>
              <Button variant="primary" type="button" size="sm" onClick={deselectAll}>
                Deselect All
              </Button>{" "}
            </Nav.Item>
          </Nav>
        </Col>
      </Row>

      <Row>
        <Col>
          <b>
            {sales ? sales.length : "0"} of {sales && sales.length > 0 ? sales[0].result_count || sales.length : "0"} records returned.
          </b>
        </Col>
      </Row>

      <Row>
        <Col sm={12} md={12} lg={12}>
          {!isLoading && search_count === 0 && (
            <div className="alert alert-secondary">Begin by entering search criteria or just clicking 'Search'</div>
          )}

          {!isLoading && search_count > 0 && (
            <GenericTable
              columns={columns}
              data={sales || []}
              pageSize={PAGE_SIZES.slmSaleSearch}
              enablePagination={true}
              hasHeaderSorting={true}
              section="slm-sale-search"
              elementIds={tableElementIds}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}

export default SLMSaleList;
